import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import TabHeader from "../../common/TabHeader";
import Loading from "../../common/Loading";
import NoContent from "../../common/NoContent";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import { getReadableDate } from "../../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../../style";
import { getExamSeriesStatus } from "../../exam_series/examSeriesStatus";

export default function ExamHistory() {
    const { userId } = useOutletContext();
    const navigate = useNavigate();
    const { requestAPI } = useAppContext();
    const { id: loggedInUserId } = useSelector((state) => state.stateUser);

    const [examSeries, setExamSeries] = useState();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: `users/${userId}/exam-series`,
            requestMethod: "GET",
            setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setExamSeries(data);
                    setError();
                } else {
                    setError(data?.error || "Couldn't load exam history");
                }
            },
        });
    }, [requestAPI, userId]);

    const openExamSeriesResult = useCallback(
        (examSeriesId) => {
            if (!examSeriesId) return;

            const isSelf = Number(loggedInUserId) === Number(userId);
            if (isSelf) {
                navigate(`/exam-series/${examSeriesId}/result`);
                return;
            }

            navigate(`/manage-exam-series/${examSeriesId}/users/${userId}/submissions`);
        },
        [loggedInUserId, navigate, userId],
    );

    return (
        <div className="flex flex-column h-full min-h-0">
            <TabHeader
                className="px-3 pt-3"
                title="Exam History"
                highlights={[`Total - ${examSeries?.length ?? 0} Exam Series`]}
            />
            <Divider />
            <div className="flex-1 min-h-0 px-3 pb-2 overflow-y-scroll gap-2 flex flex-column">
                {loading ? (
                    <Loading message="Loading Exam History" />
                ) : error ? (
                    <NoContent error={error} />
                ) : examSeries?.length ? (
                    examSeries.map((series) => {
                        const status = getExamSeriesStatus({ start_at: series.start_at, end_at: series.end_at });
                        return (
                            <div
                                key={series.id}
                                className="border-1 border-gray-300 border-round py-2 px-3 flex flex-column gap-2 cursor-pointer"
                                onClick={() => openExamSeriesResult(series.id)}
                            >
                                <div className="flex align-items-center justify-content-between gap-2">
                                    <span className={`${TEXT_NORMAL} font-semibold`}>{series.title || `Exam Series #${series.id}`}</span>
                                    <Tag value={status.label} severity={status.severity} pt={{ value: { className: TEXT_SMALL } }} />
                                </div>
                                {series.course_title && (
                                    <span className={`${TEXT_SMALL} text-color-secondary`}>
                                        <i className="pi pi-book mr-1" />
                                        {series.course_title}
                                    </span>
                                )}
                                <span className={`${TEXT_SMALL} text-color-secondary`}>
                                    <i className="pi pi-calendar mr-1" />
                                    {getReadableDate({ date: series.start_at })} - {getReadableDate({ date: series.end_at })}
                                </span>
                                <div className="flex flex-wrap gap-3">
                                    <span className={`${TEXT_SMALL} text-color-secondary`}>
                                        <i className="pi pi-pencil mr-1" />
                                        Exams attempted: {Number(series.exams_attempted) || 0}
                                    </span>
                                    <span className={`${TEXT_SMALL} text-color-secondary`}>
                                        <i className="pi pi-chart-bar mr-1" />
                                        Total marks: {Number(series.total_marks) || 0}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <NoContent error="No given exams found" />
                )}
            </div>
        </div>
    );
}
